import InfoBlock from '@components/common/info_block';
import styles from './styles.module.scss';
import { Icon } from '@components/icons';
import Typography from '@components/common/typography';
import Switch from '@components/common/switch';
import { useUserContext } from '@hooks/context/useUserContext';
import { ETwoFAMethod } from '@xeppt/xeppt-sdk/types';
import { useSettingsModals } from '@hooks/modals/useSettingsModals';
import Button from '@components/common/button';
import Info from '@components/common/info';
import { routes } from '@const/routes';
import { useNavigate } from 'react-router-dom';
import { useLocales } from '@hooks/helpers/useLocales';

const MfaSection = () => {
    const { user } = useUserContext();
    const { submitLocale } = useLocales();
    const navigate = useNavigate();
    const {
        handleSetPhoneMFA,
        handleSetEmailMFA,
        handleEnableMFA,
        handleDisableMFA: handleDisableMFAModal
    } = useSettingsModals();

    return (
        <div className={styles.wrapper}>
            <p className={styles.description}>
                When you sign in with 2FA enabled, you will be prompted to enter a verification
                code, depending on which method you choose below
            </p>
            {user?.two_fa_method === ETwoFAMethod.NONE && (
                <InfoBlock variant="warning" className={styles.info}>
                    <p className={styles.title}>Two-factor authentication is not enabled</p>
                    <p className={styles.description}>
                        We recommend enabling two-factor authentication to add an extra layer of
                        security to your account. Turn on SMS and/or Email Authentication to keep
                        your account secure.
                    </p>
                </InfoBlock>
            )}
            <div className={styles.methods_wrapper}>
                <div className={styles.container}>
                    <div className={styles.header}>
                        <div className={styles.left}>
                            <div className={styles.icon}>
                                <Icon name="sms" />
                            </div>
                            <div className={styles.text_wrapper}>
                                <Typography
                                    className={styles.title}
                                    variant="body1"
                                    lh="120%"
                                    weight="semibold">
                                    SMS
                                </Typography>
                                <p className={styles.description}>
                                    When SMS is enabled, we can send a code to your primary phone
                                    number
                                </p>
                            </div>
                        </div>
                        <Switch
                            checked={user?.two_fa_method === ETwoFAMethod.SMS}
                            onClick={() => {
                                if (user?.two_fa_method === ETwoFAMethod.SMS) {
                                    handleDisableMFAModal(ETwoFAMethod.SMS);
                                } else {
                                    handleEnableMFA(ETwoFAMethod.SMS, handleSetPhoneMFA);
                                }
                            }}
                        />
                    </div>
                    <div className={styles.content}>
                        <div className={styles.row}>
                            <div className={styles.data_wrapper}>
                                <p className={styles.label}>Your primary phone number</p>
                                <p className={styles.value}>
                                    {user?.phone_code?.includes('+')
                                        ? user?.phone_code
                                        : `+${user?.phone_code}`}{' '}
                                    {user?.phone_number}
                                </p>
                            </div>
                        </div>
                    </div>
                </div>
                <div className={styles.container}>
                    <div className={styles.header}>
                        <div className={styles.left}>
                            <div className={styles.icon}>
                                <Icon name="email" />
                            </div>
                            <div className={styles.text_wrapper}>
                                <Typography
                                    className={styles.title}
                                    variant="body1"
                                    lh="120%"
                                    weight="semibold">
                                    Email
                                </Typography>
                                <p className={styles.description}>
                                    When Email is enabled, we can send a code to your primary email
                                    address
                                </p>
                            </div>
                        </div>
                        <Switch
                            checked={user?.two_fa_method === ETwoFAMethod.EMAIL}
                            onClick={() => {
                                if (user?.two_fa_method === ETwoFAMethod.EMAIL) {
                                    handleDisableMFAModal(ETwoFAMethod.EMAIL);
                                } else {
                                    handleEnableMFA(ETwoFAMethod.EMAIL, handleSetEmailMFA);
                                }
                            }}
                        />
                    </div>
                    <div className={styles.content}>
                        <div className={styles.row}>
                            <div className={styles.data_wrapper}>
                                <p className={styles.label}>Your primary email address</p>
                                <p className={styles.value}>{user?.email}</p>
                            </div>
                        </div>
                    </div>
                </div>
                <div className={styles.container}>
                    <div className={styles.header}>
                        <div className={styles.left}>
                            <div className={styles.icon}>
                                <Icon name="phone" />
                            </div>
                            <div className={styles.text_wrapper}>
                                <Typography
                                    className={styles.title}
                                    variant="body1"
                                    lh="120%"
                                    weight="semibold">
                                    Authenticator app
                                </Typography>
                                <p className={styles.description}>
                                    Use an authenticator app to generate verification codes
                                </p>
                            </div>
                        </div>
                        {user?.two_fa_method === ETwoFAMethod.TOTP ? (
                            <Switch
                                checked
                                onClick={() => handleDisableMFAModal(ETwoFAMethod.TOTP)}
                            />
                        ) : (
                            <Info
                                variant="top"
                                content="You need to set up your authenticator app to enable this 2FA method">
                                <Switch checked={false} disabled />
                            </Info>
                        )}
                    </div>
                    <div className={styles.content}>
                        <Typography variant="body4">
                            {user?.two_fa_method !== ETwoFAMethod.TOTP
                                ? 'To enable 2FA with an authenticator app, you’ll need to set it up with your XEPPT account by scanning a QR code or entering a setup key'
                                : 'Authenticator app currently connected with your XEPPT account'}
                        </Typography>
                    </div>
                    {user?.two_fa_method !== ETwoFAMethod.TOTP && (
                        <Button
                            variant="primary"
                            onClick={() =>
                                handleEnableMFA(ETwoFAMethod.TOTP, () =>
                                    navigate(routes.settings_mfa)
                                )
                            }>
                            {submitLocale('set_up_authenticator')}
                        </Button>
                    )}
                </div>
            </div>
        </div>
    );
};

export default MfaSection;
